import { Controller } from 'react-hook-form';
import { Alert, Box, Grid, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { CtaButton, SelectInput, TextInput } from '@tassomutuo-backoffice/ui';

import { CapAutocompleteInput } from '../../../../components';
import { createPrestitoLead } from '../../../../services/api/acquisition/prestitoAcquisition';
import AcquisitionConfirmDialog from '../AcquisitionConfirmDialog';
import AcquisitionCustomerFields from '../AcquisitionCustomerFields';
import AcquisitionFormActions from '../AcquisitionFormActions';
import AcquisitionFormSection from '../AcquisitionFormSection';
import AcquisitionRecoveryPanel from '../AcquisitionRecoveryPanel';
import {
  acquisitionAmountInput,
  acquisitionAmountToNumber,
} from '../acquisitionAmount';
import { ACQUISITION_PRODUCT_LABEL } from '../acquisitionProducts';
import { AcquisitionActivityStore } from '../acquisitionActivity';
import { AcquisitionSessionStore } from '../acquisitionSessions';
import {
  acquisitionIncomeRule,
  acquisitionPensionRule,
  acquisitionRequestedAmountRule,
} from '../acquisitionValidation';
import { useAcquisitionPanel } from '../useAcquisitionPanel';
import {
  PRESTITO_INELIGIBILITY_MESSAGE,
  prestitoIneligibility,
} from './prestitoEligibility';
import { describePrestitoAcquisitionFailure } from './prestitoErrors';
import {
  PRESTITO_ASKS,
  PRESTITO_DEFAULT_VALUES,
  PrestitoConditionalField,
  PrestitoFormValues,
  buildPrestitoCommand,
  prestitoInactiveFields,
} from './prestitoForm';
import {
  PRESTITO_EMPLOYEES_NUMBER_OPTIONS,
  PRESTITO_EMPLOYMENT_CONTRACT_OPTIONS,
  PRESTITO_LOAN_PURPOSE_OPTIONS,
  PRESTITO_TENURE_OPTIONS,
  PRESTITO_YES_NO_OPTIONS,
} from './prestitoOptions';
import {
  PRESTITO_REVIEW_NOTICE,
  PRESTITO_REVIEW_TITLE,
  prestitoReviewSections,
} from './prestitoReview';

/** The answers the branching and the qualification policy read. */
const WATCHED_ANSWERS = [
  'employmentContract',
  'yearsTaxNumber',
  'hasGuarantor',
  'monthlyIncomePensioner',
  'hasOtherFinancing',
  'hasLatePreviousLoan',
  'employeesNumber',
] as const;

const PRODUCT = 'prestiti' as const;

interface IProps {
  /** Where this product's unfinished draft and command identity outlive the tab. */
  sessions: AcquisitionSessionStore;
  /** What the workspace is told about this compilation while it is mounted. */
  activity: AcquisitionActivityStore;
}

/**
 * Assisted Prestito acquisition: one page, four sections, and one authenticated
 * command that Tasso Quinto owns end to end. Nothing the public funnel wraps
 * around the same questions — consent, attribution, consultant and agency
 * routing, tracking, FIDO, autosave — is collected or sent, because the
 * backend schema is closed and would refuse it rather than ignore it.
 */
const PrestitoPanel = ({ sessions, activity }: IProps) => {
  const {
    control,
    errors,
    formRef,
    onSubmit,
    answers,
    blockingMessage,
    reviewedCommand,
    isPending,
    isBusy,
    isLocked,
    incompleteNotice,
    submissionError,
    recovery,
    confirmAcquisition,
    cancelReview,
  } = useAcquisitionPanel({
    product: PRODUCT,
    sessions,
    activity,
    defaultValues: PRESTITO_DEFAULT_VALUES,
    watch: [...WATCHED_ANSWERS],
    inactiveFields: prestitoInactiveFields,
    ineligibility: (values) => {
      const reason = prestitoIneligibility(values);
      return reason ? PRESTITO_INELIGIBILITY_MESSAGE[reason] : null;
    },
    buildCommand: buildPrestitoCommand,
    createLead: createPrestitoLead,
    describeFailure: describePrestitoAcquisitionFailure,
  });

  const asks = (field: PrestitoConditionalField) =>
    PRESTITO_ASKS[field](answers);

  return (
    <>
      <Typography color="text.secondary" mb={3}>
        Registra una lead prestito. La lead entra centralmente in Programma
        Tasso come Lead in gestione.
      </Typography>

      <AcquisitionRecoveryPanel
        productLabel={ACQUISITION_PRODUCT_LABEL[PRODUCT]}
        notice={incompleteNotice}
        recovery={recovery}
      />
      {submissionError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submissionError}
        </Alert>
      )}

      <Box component="form" ref={formRef} onSubmit={onSubmit} noValidate>
        <fieldset
          disabled={isBusy || isLocked}
          style={{ border: 0, padding: 0, margin: 0 }}
        >
          <AcquisitionFormSection
            title="Richiesta"
            description="Importo e finalità del prestito."
            icon={<RequestQuoteOutlinedIcon />}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="loanAmount"
                  rules={{
                    required: "Inserisci l'importo richiesto",
                    validate: (value) =>
                      acquisitionRequestedAmountRule(
                        acquisitionAmountToNumber(value)
                      ),
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Importo richiesto"
                      showEuroIcon
                      error={!!errors.loanAmount}
                      helperText={errors.loanAmount?.message}
                      {...field}
                      onChange={(e: { target: { value: string } }) =>
                        field.onChange(acquisitionAmountInput(e.target.value))
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="loanPurpose"
                  rules={{ required: 'Seleziona la finalità del prestito' }}
                  render={({ field }) => (
                    <SelectInput
                      label="Finalità del prestito"
                      selectOptions={PRESTITO_LOAN_PURPOSE_OPTIONS}
                      error={!!errors.loanPurpose}
                      helperText={errors.loanPurpose?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AcquisitionFormSection>

          <AcquisitionFormSection
            title="Situazione lavorativa e requisiti"
            description="Le risposte determinano le domande successive e l'idoneità del cliente."
            icon={<WorkOutlineIcon />}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="employmentContract"
                  rules={{ required: 'Seleziona il contratto di lavoro' }}
                  render={({ field }) => (
                    <SelectInput
                      label="Contratto di lavoro"
                      selectOptions={PRESTITO_EMPLOYMENT_CONTRACT_OPTIONS}
                      error={!!errors.employmentContract}
                      helperText={errors.employmentContract?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              {asks('yearsTaxNumber') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="yearsTaxNumber"
                    defaultValue=""
                    rules={{ required: 'Seleziona gli anni di attività' }}
                    render={({ field }) => (
                      <SelectInput
                        label="Anni di attività con partita IVA"
                        selectOptions={PRESTITO_TENURE_OPTIONS}
                        error={!!errors.yearsTaxNumber}
                        helperText={errors.yearsTaxNumber?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('hasGuarantor') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="hasGuarantor"
                    defaultValue=""
                    rules={{ required: 'Indica se è presente un garante' }}
                    render={({ field }) => (
                      <SelectInput
                        label="Garante"
                        selectOptions={PRESTITO_YES_NO_OPTIONS}
                        error={!!errors.hasGuarantor}
                        helperText={errors.hasGuarantor?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('yearsCurrentCompany') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="yearsCurrentCompany"
                    defaultValue=""
                    rules={{
                      required: 'Seleziona gli anni nell’azienda attuale',
                    }}
                    render={({ field }) => (
                      <SelectInput
                        label="Anni nell’azienda attuale"
                        selectOptions={PRESTITO_TENURE_OPTIONS}
                        error={!!errors.yearsCurrentCompany}
                        helperText={errors.yearsCurrentCompany?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('monthlyIncome') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="monthlyIncome"
                    defaultValue=""
                    rules={{
                      required: 'Inserisci il reddito mensile',
                      validate: (value) =>
                        acquisitionIncomeRule(acquisitionAmountToNumber(value)),
                    }}
                    render={({ field }) => (
                      <TextInput
                        label="Reddito mensile"
                        showEuroIcon
                        error={!!errors.monthlyIncome}
                        helperText={errors.monthlyIncome?.message}
                        {...field}
                        onChange={(e: { target: { value: string } }) =>
                          field.onChange(acquisitionAmountInput(e.target.value))
                        }
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('monthlyIncomePensioner') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="monthlyIncomePensioner"
                    defaultValue=""
                    rules={{
                      required: 'Inserisci la pensione mensile',
                      validate: (value) =>
                        acquisitionPensionRule(
                          acquisitionAmountToNumber(value)
                        ),
                    }}
                    render={({ field }) => (
                      <TextInput
                        label="Pensione mensile"
                        showEuroIcon
                        error={!!errors.monthlyIncomePensioner}
                        helperText={errors.monthlyIncomePensioner?.message}
                        {...field}
                        onChange={(e: { target: { value: string } }) =>
                          field.onChange(acquisitionAmountInput(e.target.value))
                        }
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="hasOtherFinancing"
                  rules={{
                    required:
                      'Indica se il cliente ha mai avuto altri finanziamenti',
                  }}
                  render={({ field }) => (
                    <SelectInput
                      label="Ha mai avuto altri finanziamenti?"
                      selectOptions={PRESTITO_YES_NO_OPTIONS}
                      error={!!errors.hasOtherFinancing}
                      helperText={errors.hasOtherFinancing?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              {asks('hasLatePreviousLoan') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="hasLatePreviousLoan"
                    defaultValue=""
                    rules={{
                      required: 'Indica se ci sono stati ritardi nei pagamenti',
                    }}
                    render={({ field }) => (
                      <SelectInput
                        label="Ritardi nei pagamenti"
                        selectOptions={PRESTITO_YES_NO_OPTIONS}
                        error={!!errors.hasLatePreviousLoan}
                        helperText={errors.hasLatePreviousLoan?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('employeesNumber') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="employeesNumber"
                    defaultValue=""
                    rules={{ required: 'Seleziona il numero di dipendenti' }}
                    render={({ field }) => (
                      <SelectInput
                        label="Dipendenti dell’azienda"
                        selectOptions={PRESTITO_EMPLOYEES_NUMBER_OPTIONS}
                        error={!!errors.employeesNumber}
                        helperText={errors.employeesNumber?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>

            {blockingMessage && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                {blockingMessage}
              </Alert>
            )}
          </AcquisitionFormSection>

          <AcquisitionFormSection
            title="Residenza"
            description="Comune di residenza del cliente."
            icon={<PlaceOutlinedIcon />}
          >
            <Box maxWidth={400}>
              <Controller
                control={control}
                name="location"
                rules={{
                  validate: (value) =>
                    !!value ||
                    "Seleziona una località dai risultati dell'autocomplete",
                }}
                render={({ field, fieldState }) => (
                  <CapAutocompleteInput
                    id="prestito-location"
                    label="Cerca per CAP o comune"
                    value={field.value}
                    onChange={(option) => field.onChange(option?.value ?? null)}
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </AcquisitionFormSection>

          <AcquisitionFormSection
            title="Cliente"
            description="Dati anagrafici e recapiti del cliente."
            icon={<PersonOutlineIcon />}
          >
            <AcquisitionCustomerFields<PrestitoFormValues>
              control={control}
              idPrefix="prestito"
            />
          </AcquisitionFormSection>

          <AcquisitionFormActions>
            <CtaButton
              variant="contained"
              type="submit"
              loading={isPending}
              disabled={isBusy || isLocked || !!blockingMessage}
            >
              Registra lead Prestito
            </CtaButton>
          </AcquisitionFormActions>
        </fieldset>
      </Box>

      {reviewedCommand && (
        <AcquisitionConfirmDialog
          open
          title={PRESTITO_REVIEW_TITLE}
          notice={PRESTITO_REVIEW_NOTICE}
          sections={prestitoReviewSections(reviewedCommand)}
          pending={isPending}
          onEdit={cancelReview}
          onConfirm={confirmAcquisition}
        />
      )}
    </>
  );
};

export default PrestitoPanel;
